import { useQuery } from "@apollo/client";
import { Alert, Box, Tooltip, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { GetScoutConversationTurnContextsForStaffQuery } from "generated-graphql/graphql";

import { GET_SCOUT_CONVERSATION_TURN_CONTEXTS_FOR_STAFF } from "./scoutContextAnalysisQueries";

type TurnContextRow =
  GetScoutConversationTurnContextsForStaffQuery["getScoutConversationTurnContextsForStaff"][number];

const columns: CustomGridColDef<TurnContextRow>[] = [
  { field: "turnNumber", headerName: "Turn", width: 80, type: "number" },
  {
    field: "text",
    headerName: "Turn context",
    flex: 1,
    minWidth: 300,
    sortable: false,
    renderCell: ({ row, hasFocus }) => {
      if (!row.text) {
        return <Typography color="text.secondary">No context recorded</Typography>;
      }
      return (
        <Tooltip
          title={row.text}
          placement="bottom-start"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: "min(800px, 80vw)",
                maxHeight: "60vh",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontFamily: "monospace",
                fontSize: 12,
                p: 2,
              },
            },
          }}
        >
          <Typography variant="body2" noWrap tabIndex={hasFocus ? 0 : -1} sx={{ width: "100%" }}>
            {row.text.replace(/\s+/g, " ")}
          </Typography>
        </Tooltip>
      );
    },
  },
];

export function TurnContextPanel({ conversationId }: { conversationId: string }) {
  const { data, loading, error } = useQuery(GET_SCOUT_CONVERSATION_TURN_CONTEXTS_FOR_STAFF, {
    variables: { conversationId },
    fetchPolicy: "cache-and-network",
  });

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {error && <Alert severity="error">Failed to load turn contexts: {error.message}</Alert>}
      <DataGrid<TurnContextRow>
        rows={data?.getScoutConversationTurnContextsForStaff ?? []}
        getRowId={(row) => row.turnNumber}
        columns={columns}
        loading={loading && !data}
        hideFooter
        disableRowSelectionOnClick
        noRowsMessage="No turns recorded yet."
        sx={{ border: "none", flex: 1 }}
      />
    </Box>
  );
}
